import apiClient from './client'

import type {
  ComunicacionEntry,
  ComunicacionEntryInput,
  ComunicacionEntryListItem,
  ComunicacionFilterOptions,
  ComunicacionPaginatedResponse,
  ComunicacionTodaySummary,
  FamiliarAccessToken
} from '@/types/gia'

interface ComunicacionFilters {
  page?: number
  centro?: number
  persona?: number
  fecha?: string
  fecha_from?: string
  fecha_to?: string
  tiene_incidencia?: boolean
  has_mensaje_familia?: boolean
}

const comunicacionApi = {
  list: async (filters: ComunicacionFilters = {}): Promise<ComunicacionPaginatedResponse> => {
    const response = await apiClient.get<ComunicacionPaginatedResponse>('/comunicacion/', { params: filters })

    return response.data
  },

  get: async (id: number): Promise<ComunicacionEntry> => {
    const response = await apiClient.get<ComunicacionEntry>(`/comunicacion/${id}/`)

    return response.data
  },

  create: async (data: ComunicacionEntryInput): Promise<ComunicacionEntry> => {
    const response = await apiClient.post<ComunicacionEntry>('/comunicacion/', data)

    return response.data
  },

  update: async (id: number, data: Partial<ComunicacionEntryInput>): Promise<ComunicacionEntry> => {
    const response = await apiClient.put<ComunicacionEntry>(`/comunicacion/${id}/`, data)

    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/comunicacion/${id}/`)
  },

  listFilterOptions: async (filters: { centro?: number } = {}): Promise<ComunicacionFilterOptions> => {
    const response = await apiClient.get<ComunicacionFilterOptions>('/comunicacion/filter-options/', { params: filters })

    return response.data
  },

  todaySummary: async (centro?: number): Promise<ComunicacionTodaySummary> => {
    const response = await apiClient.get<ComunicacionTodaySummary>('/comunicacion/today-summary/', {
      params: centro ? { centro } : {}
    })

    return response.data
  },

  // Adjuntos
  uploadAdjunto: async (entradaId: number, file: File, descripcion?: string): Promise<void> => {
    const formData = new FormData()

    formData.append('entrada', String(entradaId))
    formData.append('archivo', file)
    if (descripcion) formData.append('descripcion', descripcion)

    await apiClient.post('/comunicacion-adjuntos/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  deleteAdjunto: async (id: number): Promise<void> => {
    await apiClient.delete(`/comunicacion-adjuntos/${id}/`)
  },

  // Tokens de acceso familiar
  listTokens: async (): Promise<FamiliarAccessToken[]> => {
    const response = await apiClient.get<FamiliarAccessToken[]>('/familiar-tokens/')

    return response.data
  },

  generateToken: async (relacionId: number, pin?: string): Promise<FamiliarAccessToken> => {
    const response = await apiClient.post<FamiliarAccessToken>('/familiar-tokens/generate/', {
      relacion: relacionId,
      pin: pin || ''
    })

    return response.data
  },

  revokeToken: async (id: number): Promise<void> => {
    await apiClient.post(`/familiar-tokens/${id}/revoke/`)
  },

  regenerateToken: async (id: number): Promise<FamiliarAccessToken> => {
    const response = await apiClient.post<FamiliarAccessToken>(`/familiar-tokens/${id}/regenerate/`)

    return response.data
  }
}

export default comunicacionApi
